import { Button } from "./ui/button";
import { nativeReturnOrigin } from "../lib/api";
import { Capacitor } from "@capacitor/core";

/**
 * GoogleSignInButton — uses Emergent's managed Google OAuth.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 *
 * Web (PWA): `window.location.href` navigates the page — Emergent redirects
 * back to `/auth/callback#session_id=…` which exchanges the session.
 *
 * Native (iOS/Android, Capacitor): opens the auth URL inside an in-app
 * SFSafariViewController / Chrome Custom Tab via `@capacitor/browser`.
 * This satisfies Apple Guideline 4 (Design → Sign in with external
 * browser is a rejection reason) — the user stays inside the app the
 * whole time.
 *
 * When Emergent redirects Safari-View to `/auth/callback`, that page
 * fires `binshare://auth?session_id=…`. iOS captures the custom scheme,
 * closes the SFSafariViewController automatically, and delivers the URL
 * to the app via `App.addListener("appUrlOpen")` in App.js which
 * completes the token exchange and lands the user on /dashboard.
 */
export default function GoogleSignInButton() {
  const startGoogleAuth = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${nativeReturnOrigin()}/auth/callback`;
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    if (Capacitor.isNativePlatform()) {
      try {
        const { Browser } = await import("@capacitor/browser");
        // presentationStyle 'popover' → SFSafariViewController (in-app), not
        // full Safari. When binshare:// fires from inside the sheet, iOS
        // closes it automatically and delivers the URL via appUrlOpen.
        await Browser.open({ url: authUrl, presentationStyle: "popover" });
      } catch {
        // Extreme fallback (Capacitor Browser plugin failed) — direct nav.
        window.location.href = authUrl;
      }
      return;
    }
    window.location.href = authUrl;
  };

  return (
    <Button
      data-testid="google-signin-btn"
      type="button"
      onClick={startGoogleAuth}
      className="w-full rounded-full bg-white hover:bg-neutral-50 text-[#1A1A1A] border border-[#E2DFD8] min-h-[44px] flex items-center justify-center gap-2 shadow-sm"
    >
      <GoogleGlyph />
      <span>Continue with Google</span>
    </Button>
  );
}

function GoogleGlyph() {
  // Multicolour Google "G" — matches Google's Identity Branding Guidelines.
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.4 4 9.8 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.1c-2 1.4-4.5 2.3-7.2 2.3-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.2 5.1c-.4.4 6.7-4.9 6.7-14.8 0-1.3-.1-2.3-.4-3.5z"
      />
    </svg>
  );
}
